import { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from 'react'

const ToastContext = createContext(null)

// Cuánto vive cada aviso. Los errores, más: hay que poder leerlos antes de que
// se vayan (lo que `PlanesAdminPage.jsx` resolvía con su propio temporizador
// de 6 s, report qfo3pRfwUVUILPrQVfbj).
export const DURACION_TOAST_MS = { error: 6000, otro: 4000 }

export function useToast() {
  return useContext(ToastContext)
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  // Contador y no `Date.now()`: dos avisos en el mismo milisegundo compartían
  // id, y el temporizador del primero se llevaba a los dos.
  const proximoId = useRef(0)

  const addToast = useCallback((type, message) => {
    const id = ++proximoId.current
    setToasts(prev => [...prev, { id, type, message }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, type === 'error' ? DURACION_TOAST_MS.error : DURACION_TOAST_MS.otro)
  }, [])

  // MEMOIZADO, y no es micro-optimización: el provider re-renderiza cada vez
  // que un toast aparece o se va (estado + su temporizador). Sin el memo, el
  // objeto de contexto cambia de identidad en ese momento y los 35 arrays de
  // deps del repo que tienen `toast` (en 25 archivos — contados el 7-oct-2026;
  // antes acá decía 22, de una medición anterior) se vuelven a disparar. El
  // caso que lo vuelve grave es el que CIERRA el ciclo: un `useCallback` con deps
  // `[toast]` cuyo `catch` llama `toast.error` (molde vivo en
  // `src/modules/plataforma/pages/AIAprendizajePage.jsx`) — el error dispara
  // el toast, el toast cambia la identidad, el efecto vuelve a correr y vuelve
  // a fallar. Bucle de llamadas a callables contra un endpoint ya roto, sin
  // tope: no es jank, es plata.
  // `addToast` es `useCallback(..., [])`, así que el memo queda estable de por
  // vida; la dep explícita es la que satisface `exhaustive-deps` sin supresión.
  const toast = useMemo(() => ({
    success: (msg) => addToast('success', msg),
    error: (msg) => addToast('error', msg),
    info: (msg) => addToast('info', msg),
  }), [addToast])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {/* Que un lector de pantalla anuncie el aviso: el propio de PlanesAdminPage
          llevaba `role="status"` y migrarlo no lo pierde. `aria-live` y no un
          `role`: las pantallas buscan por rol sus propios `status`/`alert`, y
          un aviso a la vista no se los tiene que tapar. */}
      <div aria-live="polite" className="fixed top-6 right-6 z-[500] flex flex-col gap-2">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`px-5 py-3.5 rounded-xl text-sm font-medium shadow-xl animate-fade-in max-w-[380px] ${
              t.type === 'success' ? 'bg-emerald-900 text-emerald-200 border border-green-500/20' :
              t.type === 'error' ? 'bg-red-950 text-red-200 border border-red-500/20' :
              'bg-blue-950 text-blue-200 border border-blue-500/20'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}
